// LRU cache + in-flight dedup + write-through fetch. Composes
// createLruCache with a Map<key, Promise<V>> so concurrent
// `getOrFetch` calls for the same key share one network round-trip
// AND the resolved value lands in the cache for subsequent reads.
//
// Extracted from voyage-details/store.tsx where the pattern lived
// inline; the overrides cache reuses the same shape, and any future
// per-key async resource (route data, port catalogues, etc.) can
// drop in without re-implementing the dedup.
//
// Failure semantics: the fetcher's rejection bubbles up to the
// caller AND the in-flight entry is cleared in a `finally` so a
// retry refires. The cache is NOT poisoned on failure — only
// successful resolutions land there.

import { createLruCache } from "./lru";

export interface AsyncCacheOptions<V> {
  fetcher: (key: string) => Promise<V>;
  maxSize: number;
  // Fired after the cache's size changes (post-eviction). Pipes
  // straight to the underlying LRU; same signal/setter that updates
  // the status-bar "N details loaded" suffix in the voyage-details
  // store.
  onSize?: (size: number) => void;
}

export interface AsyncCache<V> {
  /** Cache-or-fetch. Returns the cached value if present; otherwise
   *  fires the fetcher (deduping concurrent calls for the same key)
   *  and caches the result on success. */
  getOrFetch(key: string): Promise<V>;
  /** Synchronous read. */
  get(key: string): V | undefined;
  /** Force-set, bypassing fetch. Useful when a fetched value was
   *  produced elsewhere (e.g. backend echoed a save) and the cache
   *  should reflect the new value without a refetch. */
  set(key: string, value: V): void;
  has(key: string): boolean;
  /** Iterate cached values in insertion order. Inherited from the
   *  underlying LRU — does NOT refresh LRU order on iteration. */
  values(): IterableIterator<V>;
}

export function createAsyncCache<V>(opts: AsyncCacheOptions<V>): AsyncCache<V> {
  // Spread-then-omit so `onSize` is only present when defined — strict
  // `exactOptionalPropertyTypes` rejects passing `undefined` through.
  const lruOpts: { maxSize: number; onSize?: (size: number) => void } = { maxSize: opts.maxSize };
  if (opts.onSize) lruOpts.onSize = opts.onSize;
  const cache = createLruCache<string, V>(lruOpts);
  // In-flight dedup: while a fetch is pending for `key`, a second
  // caller awaits the SAME promise rather than firing a redundant
  // network call. Cleared in `finally` so a failed fetch doesn't
  // stick the key in a permanent "loading" state.
  const inFlight = new Map<string, Promise<V>>();

  function getOrFetch(key: string): Promise<V> {
    const cached = cache.get(key);
    if (cached !== undefined) return Promise.resolve(cached);
    const pending = inFlight.get(key);
    if (pending) return pending;
    const p = opts.fetcher(key)
      .then((data) => {
        cache.set(key, data);
        return data;
      })
      .finally(() => {
        inFlight.delete(key);
      });
    inFlight.set(key, p);
    return p;
  }

  return {
    getOrFetch,
    get: (k) => cache.get(k),
    set: (k, v) => cache.set(k, v),
    has: (k) => cache.has(k),
    values: () => cache.values(),
  };
}
